import { useState } from 'react';
import { tx } from '../i18n';
import { pedir } from '../lib/api';
import { permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import { Pastilla, primero, Ventana as Modal } from '../ui/piezas';

export interface EstadoSoporte {
  estado: 'ninguna' | 'pedida' | 'abierta';
  sesion: { id: string; quien: string; motivo: string; pedida: string; aprobada: string | null; aprobada_por: string | null; vence: string | null } | null;
  quedan: number | null;
}

/**
 * La sesión de soporte, desde el Centro (centro.md §3.2). Acá solo se **pide**: quien abre
 * la puerta es el cliente, en su propia web, y la corta cuando quiera. Mientras esté
 * abierta, se puede levantar un puente a cualquier cosa de su red.
 */
export function Soporte({ applianceId, enLinea, soporte, alCambiar }: { applianceId: string; enLinea: boolean; soporte: EstadoSoporte | null; alCambiar: (aviso: string) => void }) {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  const [pedir_, setPedir] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [minutos, setMinutos] = useState('120');
  const [puente, setPuente] = useState(false);
  const [destino, setDestino] = useState('');
  const [puerto, setPuerto] = useState('8291');
  const [enlace, setEnlace] = useState<string | null>(null);
  const [motor, setMotor] = useState('');
  const t = tx.soporte;
  const e = soporte?.estado ?? 'ninguna';
  // por cuáles motores se puede salir: solo hace falta elegir cuando hay más de uno
  const motores = useConsulta<{ id: string; nombre: string }[]>(`/appliances/${applianceId}/soporte/motores`, { enabled: e === 'abierta' });

  return (
    <section className="panel">
      <h2 className="rotulo">
        {t.titulo}
        <span className="aux">
          <Pastilla tono={e === 'abierta' ? 'caido' : e === 'pedida' ? 'parcial' : 'desconocido'}>{t.estados[e]}</Pastilla>
        </span>
      </h2>

      {e === 'ninguna' && <p className="nota">{t.que}</p>}

      {soporte?.sesion && e !== 'ninguna' && (
        <dl className="ficha">
          <div className="dato">
            <dt>{t.motivo}</dt>
            <dd>{soporte.sesion.motivo}</dd>
          </div>
          <div className="dato">
            <dt>{t.loPidio}</dt>
            <dd>{soporte.sesion.quien}</dd>
          </div>
          {e === 'abierta' ? (
            <>
              <div className="dato">
                <dt>{t.aprobo}</dt>
                <dd>{soporte.sesion.aprobada_por ?? '—'}</dd>
              </div>
              <div className="dato">
                <dt>{t.hasta}</dt>
                <dd>
                  {cuando(soporte.sesion.vence)} · {t.quedan(soporte.quedan ?? 0)}
                </dd>
              </div>
            </>
          ) : (
            <div className="dato">
              <dt>{t.pedida}</dt>
              <dd>{cuando(soporte.sesion.pedida)}</dd>
            </div>
          )}
        </dl>
      )}

      {e === 'pedida' && <p className="nota">{t.esperando}</p>}

      <div className="botones">
        {puede.actualizar && e === 'ninguna' && (
          <button type="button" className="btn prim" disabled={!enLinea} onClick={() => setPedir(true)}>
            {t.pedirla}
          </button>
        )}
        {puede.actualizar && e === 'abierta' && (
          <button type="button" className="btn prim" onClick={() => setPuente(true)}>
            {t.abrirPuente}
          </button>
        )}
        {puede.actualizar && e !== 'ninguna' && (
          <button
            type="button"
            className="btn peligro"
            onClick={() =>
              void pedir(`/appliances/${applianceId}/soporte/cancelar`, { metodo: 'POST' }).then(() => alCambiar(t.cancelada))
            }
          >
            {t.cancelar}
          </button>
        )}
        {!enLinea && e === 'ninguna' && <span className="nota">{t.sinContacto}</span>}
      </div>

      {pedir_ && (
        <Modal
          titulo={t.pedirla}
          boton={t.pedirBoton}
          alCerrar={() => setPedir(false)}
          alEnviar={async () => {
            const falta = primero(motivo.trim().length < 5 && t.faltaMotivo);
            if (falta) return falta;
            await pedir(`/appliances/${applianceId}/soporte`, { metodo: 'POST', cuerpo: { motivo: motivo.trim(), minutos: Number(minutos) } });
            setPedir(false);
            setMotivo('');
            alCambiar(t.pedidaOk);
          }}
        >
          <p className="nota">{t.pedirQue}</p>
          <label className="campo" htmlFor="sop-motivo">
            {t.motivo}
            <input id="sop-motivo" className="entrada" value={motivo} onChange={(ev) => setMotivo(ev.target.value)} placeholder={t.motivoEjemplo} />
            <small className="ayuda">{t.motivoAyuda}</small>
          </label>
          <label className="campo" htmlFor="sop-min">
            {t.porCuanto}
            <select id="sop-min" className="lista-sel" value={minutos} onChange={(ev) => setMinutos(ev.target.value)}>
              {['60', '120', '240', '480'].map((m) => (
                <option key={m} value={m}>
                  {t.horas(Number(m) / 60)}
                </option>
              ))}
            </select>
          </label>
        </Modal>
      )}

      {puente && (
        <Modal
          titulo={t.abrirPuente}
          boton={t.abrirBoton}
          alCerrar={() => {
            setPuente(false);
            setEnlace(null);
          }}
          alEnviar={async () => {
            const falta = primero(!destino.trim() && t.faltaDestino);
            if (falta) return falta;
            const r = await pedir<{ enlace: string }>(`/appliances/${applianceId}/soporte/puente`, {
              metodo: 'POST',
              cuerpo: { destino: destino.trim(), puerto: Number(puerto), motor: motor || undefined },
            });
            setEnlace(r.enlace);
          }}
        >
          <p className="nota">{t.puenteQue}</p>
          <label className="campo" htmlFor="sop-destino">
            {t.destino}
            <input id="sop-destino" className="entrada" value={destino} onChange={(ev) => setDestino(ev.target.value)} placeholder="10.60.1.1" />
          </label>
          <label className="campo" htmlFor="sop-puerto">
            {t.puerto}
            <input id="sop-puerto" className="entrada" type="number" value={puerto} onChange={(ev) => setPuerto(ev.target.value)} />
          </label>
          {(motores.data ?? []).length > 1 && (
            <label className="campo" htmlFor="sop-motor">
              {t.porCualMotor}
              <select id="sop-motor" className="lista-sel" value={motor} onChange={(ev) => setMotor(ev.target.value)}>
                <option value="">{t.motorSolo}</option>
                {(motores.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
              <small className="ayuda">{t.motorAyuda}</small>
            </label>
          )}
          {enlace && (
            <p className="alerta" role="status">
              <b>{t.listo}</b> {t.listoQue}
              <br />
              <a href={enlace}>{t.abrirlo}</a>
            </p>
          )}
        </Modal>
      )}
    </section>
  );
}
